import { heroFrameSet, SpriteFrameSet } from './assets';
import { Universe } from './mode';
import { TileKind } from './types';

export type HeroId =
  | 'captain-america'
  | 'black-panther'
  | 'iron-man'
  | 'spider-man'
  | 'thor'
  | 'hulk'
  | 'doctor-strange'
  | 'scarlet-witch'
  | 'captain-marvel'
  | 'vision'
  | 'ant-man'
  | 'ghost-spider'
  | 'spin'
  | 'wolverine'
  | 'rocket'
  | 'daredevil'
  | 'human-torch'
  | 'red-ranger'
  | 'green-ranger'
  | 'pink-ranger';

export type RosterHeroId = HeroId;

export type HeroSpec = {
  id: HeroId;
  displayName: string;
  tileKind: TileKind;
  idleAnimKey: string;
  attackAnimKey: string;
  idleFrames: () => SpriteFrameSet;
  attackFrames: () => SpriteFrameSet;
  universe: Universe;
};

function makeHero(
  id: HeroId,
  displayName: string,
  tileKind: TileKind,
  universe: Universe = 'marvel',
): HeroSpec {
  return {
    id,
    displayName,
    tileKind,
    idleAnimKey: `hero-${id}-idle`,
    attackAnimKey: `hero-${id}-attack`,
    idleFrames: () => heroFrameSet(id, 'idle'),
    attackFrames: () => heroFrameSet(id, 'attack'),
    universe,
  };
}

export const HEROES: Record<HeroId, HeroSpec> = {
  'captain-america': makeHero('captain-america', 'Captain America', 'shield'),
  'black-panther': makeHero('black-panther', 'Black Panther', 'claws'),
  'iron-man': makeHero('iron-man', 'Iron Man', 'repulsor'),
  'spider-man': makeHero('spider-man', 'Spider-Man', 'web'),
  thor: makeHero('thor', 'Thor', 'hammer'),
  hulk: makeHero('hulk', 'Hulk', 'fist'),
  'doctor-strange': makeHero('doctor-strange', 'Doctor Strange', 'eye'),
  'scarlet-witch': makeHero('scarlet-witch', 'Scarlet Witch', 'hex'),
  'captain-marvel': makeHero('captain-marvel', 'Captain Marvel', 'photon'),
  vision: makeHero('vision', 'Vision', 'gem'),
  'ant-man': makeHero('ant-man', 'Ant-Man', 'atom'),
  'ghost-spider': makeHero('ghost-spider', 'Ghost-Spider', 'ghost-web'),
  spin: makeHero('spin', 'Spin', 'bolt'),
  wolverine: makeHero('wolverine', 'Wolverine', 'adamantium'),
  rocket: makeHero('rocket', 'Rocket', 'blaster'),
  daredevil: makeHero('daredevil', 'Daredevil', 'billyclub'),
  'human-torch': makeHero('human-torch', 'Human Torch', 'flame'),
  'red-ranger': makeHero('red-ranger', 'Red Ranger', 'power-sword', 'powerrangers'),
  'green-ranger': makeHero('green-ranger', 'Green Ranger', 'dragon-dagger', 'powerrangers'),
  'pink-ranger': makeHero('pink-ranger', 'Pink Ranger', 'power-bow', 'powerrangers'),
};

export const COMBAT_HERO_IDS: HeroId[] = [
  'captain-america',
  'black-panther',
  'iron-man',
  'spider-man',
  'thor',
  'hulk',
  'doctor-strange',
  'scarlet-witch',
  'captain-marvel',
  'vision',
  'ant-man',
  'ghost-spider',
  'spin',
  'wolverine',
  'rocket',
  'daredevil',
  'human-torch',
  'red-ranger',
  'green-ranger',
  'pink-ranger',
];

export const MARVEL_HERO_IDS: HeroId[] = COMBAT_HERO_IDS.filter(
  (id) => HEROES[id].universe === 'marvel',
);

export const POWERRANGERS_HERO_IDS: HeroId[] = COMBAT_HERO_IDS.filter(
  (id) => HEROES[id].universe === 'powerrangers',
);

export function heroByTileKind(kind: TileKind): HeroSpec | undefined {
  for (const id of Object.keys(HEROES) as HeroId[]) {
    if (HEROES[id].tileKind === kind) return HEROES[id];
  }
  return undefined;
}
